<template>
  <section
    class="c_wrapper c_accordion_v2"
    :class="{ 'c_accordion_v2--columns': data.enableColumns }"
    :style="accordionThemeVars"
  >
    <div class="c_accordion__parent_description">
      <Chip v-if="data.chip" theme="var(--color-yellow)">{{ data.chip }}</Chip>
      <h2 class="c_2022--h2_graphik" v-if="data.sectionTitle">{{ data.sectionTitle }}</h2>
      <div class="c_2022--body" v-if="data.sectionDescription" v-html="data.sectionDescription"></div>
    </div>
    <div class="c_accordion_container">
      <div
        v-for="(accordion, index) in data.accordions"
        :key="index"
        :class="{ 'c_accordion--expanded': isExpanded(index) }"
      >
        <h3>
          <button
            v-if="accordion.accordionTitle"
            class="c_accordion__button"
            @click="toggleAccordion(index)"
            :id="`c_accordion__header--${index}`"
            :aria-controls="`c_accordion__panel--${index}`"
            :aria-expanded="isExpanded(index)"
          >
            <IconSVG v-if="accordion.icon !== 'none'" v-bind="getAccordionIcon(accordion.icon)" />
            <span>{{ accordion.accordionTitle }}</span>
            <IconSVG :handle="isExpanded(index) ? 'arrow_down' : 'arrow_up'" width="15" height="20" />
          </button>
        </h3>
        <section
          class="c_accordion__panel c_2022--body c_text"
          :class="{ 'c_accordion__panel--expanded': isExpanded(index) }"
          :aria-labelledby="`c_accordion__header--${index}`"
          :aria-hidden="!isExpanded(index)"
          :inert="!isExpanded(index) ? '' : null"
          role="region"
          :id="`c_accordion__panel--${index}`"
        >
          <div v-if="accordion.accordionDescription" v-html="accordion.accordionDescription"></div>
          <ul v-if="accordion.ctaLinks" class="c_accordion__ctas">
            <li v-for="(link, index) in accordion.ctaLinks" :key="index" v-if="link.linkUrl">
              <a
                :href="link.linkUrl"
                :target="link.openInANewTab ? '_blank' : ''"
                :aria-label="link.linkLabel"
                :title="link.linkLabel"
                >{{ link.linkLabel }}</a
              >
            </li>
          </ul>
        </section>
      </div>
    </div>
  </section>
</template>

<script>
import { defineComponent, ref } from '@nuxtjs/composition-api';
import Chip from 'Components/chip/Chip.vue';
import IconSVG from 'Components/icon_svg/IconSVG.vue';

export default defineComponent({
  name: 'AccordionV2',
  props: {
    data: Object,
  },
  components: {
    Chip,
    IconSVG,
  },
  setup(props) {
    const expandedIndices = ref([]);
    const expandedIndex = ref(null);

    const isExpanded = (index) => {
      return props.data.allowMultiple ? expandedIndices.value.includes(index) : expandedIndex.value === index;
    };

    const toggleAccordion = (index) => {
      if (props.data.allowMultiple) {
        if (expandedIndices.value.includes(index)) {
          expandedIndices.value = expandedIndices.value.filter((i) => i !== index);
        } else {
          expandedIndices.value.push(index);
        }
      } else {
        expandedIndex.value = expandedIndex.value === index ? null : index;
      }
    };

    const getAccordionIcon = (icon) => {
      const icons = {
        checkmark: { handle: 'checkmark', color: '#62912D', height: '13', width: '25' },
        crossmark: { handle: 'closeV2', color: '#ED0000', height: '13', width: '25' },
      };
      return icons[icon];
    };

    const accordionThemeVars =
      props.data.colors === 'whiteTextBlackBackground'
        ? {
            '--accordion-bg': 'var(--color-black)',
            '--accordion-text': 'var(--color-white)',
            '--accordion-divider': 'var(--color-yellow)',
            '--accordion-link-text': 'var(--color-yellow)',
            '--accordion-link-hover': '#BF8900',
            '--accordion-btn-hover': 'rgba(255,255,255,0.08)',
          }
        : {
            '--accordion-bg': 'var(--color-white)',
            '--accordion-text': '#1c1c1c',
            '--accordion-divider': 'var(--color-gray_230)',
            '--accordion-link-text': 'var(--color-red)',
            '--accordion-link-hover': 'var(--color-red_179)',
            '--accordion-btn-hover': 'rgba(0,0,0,0.03)',
            
          };

    return {
      toggleAccordion,
      expandedIndices,
      expandedIndex,
      isExpanded,
      getAccordionIcon,
      accordionThemeVars,
    };
  },
});
</script>

<style lang="scss">
.c_accordion_v2 {
  color: var(--accordion-text);
  &--columns {
    @include mq($mq_sm_to_md) {
      display: flex;
      align-items: flex-start;
      justify-content: center;
      .c_accordion__parent_description {
        flex-basis: 50%;
      }
      .c_accordion_container {
        flex-basis: 50%;
      }
    }
  }
  .c_accordion__parent_description {
    margin-bottom: px_to_rem(50);
  }
  .c_2022--h2_graphik {
    @include font('graphik_semibold');
  }
  .c_accordion__item {
    margin-bottom: 1rem;
  }

  .c_accordion__button {
    cursor: pointer;
    font-weight: bold;
    padding: px_to_rem(16) 0;
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    background-color: transparent;
    border: none;
    text-align: left;
    transition: background-color cubic-bezier(0.4, 0, 0.6, 1) 0.15s;
    will-change: background-color;
    &:hover {
      background-color: var(--accordion-btn-hover);
    }
    span {
      padding-right: px_to_rem(20);
      margin-right: auto;
      line-height: px_to_rem(24);
    }
  }
  h3,
  h3 button {
    @include font('graphik_semibold');
    font-size: px_to_rem(16);
    color: var(--accordion-text);
  }
  .c_accordion_container {
    color: var(--accordion-text);
    & > div {
      border-bottom: 1px solid var(--accordion-divider);
    }
    .c_accordion__panel {
      font-size: px_to_rem(14);
      max-height: 0;
      transition:
        max-height 0.15s cubic-bezier(0.4, 0, 0.6, 1),
        padding 0.15s cubic-bezier(0.4, 0, 0.6, 1);
      overflow: hidden;
      will-change: max-height, padding;
      padding: 0;
      &--expanded {
        max-height: 100vh;
        height: auto;
        padding: px_to_rem(20) 0;
      }
      &.c_2022--body.c_text {
        --text-fz: px_to_rem(14);
        line-height: px_to_rem(18);
        p {
          margin-bottom: px_to_rem(16);
        }
        a {
          color: var(--accordion-link-text);
          &:hover,
          &:focus {
            color: var(--accordion-link-hover);
          }
        }
      }
    }
    h3 {
      .c_icon_svg {
        padding-right: px_to_rem(10);
        flex-shrink: 0;
      }
    }
  }
  .c_accordion__ctas {
    display: flex;
    gap: px_to_rem(20);
    margin-top: px_to_rem(16);
    li {
      list-style-type: none;
      margin: 0;
      a {
        @include font('graphik_semibold');
        text-decoration: none;
        font-size: px_to_rem(16);
      }
    }
  }
}
</style>
